import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  DataPlan,
  DataPlanResponse,
  DataPlanState,
  FilterOptions,
} from "./types/data-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: DataPlanState = {
  dataPlans: [],
  dataPlan: null,
  selectedDataPlan: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  filters: {},
  filterOptions: null,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Create DataPlan
export const createDataPlan = createAsyncThunk<
  DataPlanResponse,
  any,
  { rejectValue: ApiError; state: RootState }
>("dataPlans/create", async (dataPlanData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/data-plans`, dataPlanData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // 'Content-Type': 'application/json' do not dd this line, allow browser to handle
      },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data plan" },
    );
  }
});

// ✅ Fetch DataPlans (Paginated)
export const fetchDataPlans = createAsyncThunk<
  DataPlanResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("dataPlans/fetch", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 30,
      search,
      status,
      category,
      country,
      sort_by,
      sort_order,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;
      if (category) params.category = category;
      if (country) params.country = country;
      if (sort_by) params.sort_by = sort_by;
      if (sort_order) params.sort_order = sort_order;

      res = await axios.get(`${API_URL}/data-plans`, {
        params,
        headers,
      });
    }

    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data plans" },
    );
  }
});

// ✅ Fetch Single DataPlan by ID
export const fetchDataPlanById = createAsyncThunk<
  DataPlan,
  number,
  { rejectValue: ApiError; state: RootState }
>("dataPlans/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/data-plans/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch data plan" },
    );
  }
});

// ✅ Delete DataPlan
export const deleteDataPlan = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("dataPlans/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/data-plans/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete data plan" },
    );
  }
});

// ✅ Update DataPlan
export const updateDataPlan = createAsyncThunk<
  DataPlan,
  { id: number; data: Partial<DataPlan> | FormData },
  { rejectValue: ApiError; state: RootState }
>("dataPlans/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(
      `${API_URL}/data-plans/${id}?_method=PUT`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update data plan" },
    );
  }
});

// ✅ Fetch Filter Options
export const fetchFilterOptions = createAsyncThunk<
  FilterOptions,
  void,
  { rejectValue: ApiError; state: RootState }
>("dataPlans/fetchFilterOptions", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/data-plans/filter-options`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch filter options" },
    );
  }
});

// ✅ Slice
const dataPlanSlice = createSlice({
  name: "dataPlans",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    resetDataPlanState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedDataPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createDataPlan.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createDataPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.dataPlan) {
          state.dataPlans.unshift(action.payload.dataPlan);
        }
      })
      .addCase(createDataPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create data plan";
      })

      // Fetch All
      .addCase(fetchDataPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDataPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.dataPlans = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchDataPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plans";
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })

      // Fetch One
      .addCase(fetchDataPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedDataPlan = null;
      })
      .addCase(fetchDataPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedDataPlan = action.payload;
      })
      .addCase(fetchDataPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plan";
      })

      // Delete
      .addCase(deleteDataPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteDataPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.dataPlans = state.dataPlans.filter(
          (plan) => plan.id !== action.payload,
        );
      })
      .addCase(deleteDataPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete data plan";
      })

      // Update
      .addCase(updateDataPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateDataPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Update data plan in list
        state.dataPlans = state.dataPlans.map((plan) =>
          plan.id === action.payload.id ? action.payload : plan,
        );

        // Update selected data plan if it's the one updated
        if (state.selectedDataPlan?.id === action.payload.id) {
          state.selectedDataPlan = action.payload;
        }
      })
      .addCase(updateDataPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update data plan";
      });
  },
});

export const { setFilters, clearFilters, resetDataPlanState } =
  dataPlanSlice.actions;
export default dataPlanSlice.reducer;
